import { CenteredLazyLoading } from "@/controls/LazyLoading";
import {
	BAD_BARS_BG,
	BAD_BARS_HOVER_BG,
	TOO_SHORT_BAR_BG,
	TOO_SHORT_BAR_HOVER_BG,
} from "@/shared/colors";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { SxProps, Theme } from "@mui/material/styles";
import { RowClassParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useBarsTableData } from "../hooks/useBarsTableData";
import { LengthBarRow } from "../models/LengthBarRow";

const TOO_LONG_ROW_CLASS = "too-long-bar-row";
const TOO_SHORT_ROW_CLASS = "too-short-bar-row";

const getRejectedBarRowClass = (
	params: RowClassParams<LengthBarRow>,
): string | undefined => {
	if (params.data?.IsTooLong) return TOO_LONG_ROW_CLASS;
	if (params.data?.IsTooShort) return TOO_SHORT_ROW_CLASS;
	return undefined;
};

const rejectedBarStyles: SxProps<Theme> = {
	[`& .${TOO_LONG_ROW_CLASS}`]: {
		backgroundColor: BAD_BARS_BG,
	},
	[`& .${TOO_LONG_ROW_CLASS}:hover`]: {
		backgroundColor: `${BAD_BARS_HOVER_BG} !important`,
	},
	[`& .${TOO_SHORT_ROW_CLASS}`]: {
		backgroundColor: TOO_SHORT_BAR_BG,
	},
	[`& .${TOO_SHORT_ROW_CLASS}:hover`]: {
		backgroundColor: `${TOO_SHORT_BAR_HOVER_BG} !important`,
	},
};

type Props = {
	isLoading: boolean;
	rows: LengthBarRow[];
	selectedBundleId: number | null;
};

export const LengthBarsTable = ({
	isLoading,
	rows,
	selectedBundleId,
}: Props) => {
	const { columnDefs } = useBarsTableData();

	if (selectedBundleId === null) {
		return (
			<Paper
				elevation={2}
				sx={{
					height: "100%",
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
				}}
			>
				<Typography variant="body1" color="text.secondary">
					Select a bundle to view bars
				</Typography>
			</Paper>
		);
	}

	return (
		<Paper
			elevation={2}
			sx={{ height: "100%", overflow: "hidden", ...rejectedBarStyles }}
		>
			<AgGridReact<LengthBarRow>
				loading={isLoading}
				rowData={rows}
				columnDefs={columnDefs}
				animateRows
				loadingOverlayComponent={CenteredLazyLoading}
				getRowId={(params) => `${params.data.BarId}`}
				getRowClass={getRejectedBarRowClass}
				suppressContextMenu
			/>
		</Paper>
	);
};
